import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import { Form, FormGroup, TextInput, ActionGroup, PageSection, Title, Button, FormAlert, Alert, } from '@patternfly/react-core';
import { CreateToastNotification, ToastNotificationContext } from '@app/Context/ToastNotificationContext';
import EzFeedFunctions from "@app/Page/EzFeedPage/EzFeedFunctions";
import FieldsInterface from '@app/Interface/FieldsInterface';
import TypeAssertions from '@app/utils/TypeAssertions';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import { ToastNotificationType } from '@app/Interface/ToastNotificationInterface';
import EzFeedHelper from '@app/Service/EzFeedHelper';
import htmlParser from '@app/utils/htmlParser';

interface EzFeedProps {
    pageData: PageModel,
}

const EzFeedPage: React.FC<EzFeedProps> = ({ pageData, children }) => {

    // NOTE: ToastNotificationContext provider was added to `src\app\index.tsx`
    const { toastDispatch } = React.useContext(ToastNotificationContext);

    const initialFieldValues: FieldsInterface = EzFeedFunctions.getEzFeedFields();

    const [formFields, setFormFields] = React.useState(initialFieldValues);

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    const defaultFormValidated = "default";

    // for values: see https://www.patternfly.org/v4/components/form#formgroup
    const [requiredValidated, setRequiredValidated] = React.useState(defaultFormValidated);

    const handleChange = (newValue: string, e: React.FormEvent<HTMLInputElement>) => {

        const fieldId = e.currentTarget.id;

        const updatedFields = { ...formFields };

        updatedFields[fieldId].value = newValue;

        setFormFields(updatedFields);

    }

    const handleSubmit = async (e: React.SyntheticEvent) => {

        e.preventDefault();

        const validform = formValidation();

        setIsFormProcessing(true);

        if (validform) {
            
            // TODO: Add soap request
            const username = typeof formFields["ezUsername"].value === "string" ? formFields["ezUsername"].value : "";

            const password = typeof formFields["ezPassword"].value === "string" ? formFields["ezPassword"].value : "";

            const [qualityResponse, goodQualityLogin] = await EzFeedHelper.getQualityData(username, password);

            const [historicalResponse, goodHistoricalLogin] = await EzFeedHelper.getHistoricalQualityData(username, password);

            let notificationType: ToastNotificationType = ToastNotificationType.Default;

            let toastTitle = "Success";

            let toastDescription = "EZ Account is valid.";

            if (goodQualityLogin || goodHistoricalLogin) {

                toastDescription += `<br />${qualityResponse}<br />${historicalResponse}`;
            
            } else {
                
                notificationType = ToastNotificationType.Danger;

                toastTitle = "Failed";

                toastDescription = "Access denied.";

            }

            CreateToastNotification(toastDispatch, notificationType, toastTitle, toastDescription);

        }

        setIsFormProcessing(false);

    }

    /**
     * Validates form fields
     * @returns boolean
     */
    const formValidation = (): boolean => {

        let validForm = true;

        const updatedFields = { ...formFields };

        setRequiredValidated("default");

        if (typeof formFields["ezUsername"].value === "string" && TypeAssertions.isStringNullOrEmpty(formFields["ezUsername"].value)) {

            setRequiredValidated("error");

            updatedFields["ezUsername"].validated = "error";

            validForm = false;

        } else {

            updatedFields["ezUsername"].validated = "default";

        }

        if (typeof formFields["ezPassword"].value === "string" && TypeAssertions.isStringNullOrEmpty(formFields["ezPassword"].value)) {

            setRequiredValidated("error");

            updatedFields["ezPassword"].validated = "error";

            validForm = false;

        } else {

            updatedFields["ezPassword"].validated = "default";

        }

        setFormFields(updatedFields);

        return validForm;

    }

    /**
     * Resets to previous values by loading current user information
     * @param e 
     */
    const handleReset = (e: React.FormEvent<HTMLButtonElement>) => {

        e.preventDefault();

        setFormFields(initialFieldValues);

    }

    React.useEffect(() => {

        return () => {
            
            // on unmount, clear all toast messages
            toastDispatch({
                type: "CLEAR"
            });

        };

    }, [toastDispatch]);
    
    return (
        <>
            <PageSection>
                <Title headingLevel="h1" className="pf-u-mb-md">{pageData.title}</Title>
                <div className="pf-c-content">
                    {htmlParser.htmlToJSX(pageData.content ?? '')}
                </div>
                <Form autoComplete="false" isWidthLimited>
                    {requiredValidated === "error" && (
                        <FormAlert>
                            <Alert
                                variant="danger"
                                title="Fill out all required fields before continuing"
                                aria-live="polite"
                                role="alert"
                                isInline
                            ></Alert>
                        </FormAlert>
                    )}
                    <FormGroup
                        label={formFields["ezUsername"].label}
                        isRequired={formFields["ezUsername"].required}
                        fieldId={formFields["ezUsername"].formFieldId}
                        helperTextInvalid={formFields["ezUsername"].helperTextInvalid}
                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                        validated={formFields["ezUsername"].validated}
                    >
                        <TextInput
                            isRequired={formFields["ezUsername"].required}
                            type="text"
                            id={formFields["ezUsername"].fieldId}
                            name={formFields["ezUsername"].fieldId}
                            value={typeof formFields["ezUsername"].value === "string" ? formFields["ezUsername"].value : ""}
                            onChange={handleChange}
                        />
                    </FormGroup>
                    <FormGroup
                        label={formFields["ezPassword"].label}
                        isRequired={formFields["ezPassword"].required}
                        fieldId={formFields["ezPassword"].formFieldId}
                        helperTextInvalid={formFields["ezPassword"].helperTextInvalid}
                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                        validated={formFields["ezPassword"].validated}
                    >
                        <TextInput
                            isRequired={formFields["ezPassword"].required}
                            type="password"
                            id={formFields["ezPassword"].fieldId}
                            name={formFields["ezPassword"].fieldId}
                            value={typeof formFields["ezPassword"].value === "string" ? formFields["ezPassword"].value : ""}
                            onChange={handleChange}
                        />
                    </FormGroup>
                    <ActionGroup>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            spinnerAriaValueText={isFormProcessing ? "Testing" : undefined}
                            isLoading={isFormProcessing}
                        >{isFormProcessing ? "Testing" : "Submit"}</Button>
                        <Button variant="link" onClick={handleReset}>Reset</Button>
                    </ActionGroup>
                </Form>
                {children}
            </PageSection>
        </>
    )

};

export default EzFeedPage;